var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useJobKeyUrlSync } from "@/hooks/useJobKeyUrlSync";
import { Box, Button, Dialog, Flex, Portal, useSlotRecipe, } from "@chakra-ui/react";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState } from "react";
import { SearchResultsJobDetails, SearchResultsListPanel, } from "@/components/SearchResults";
import { useDetectMobileViewPort } from "../../hooks/useDetectMobileViewPort";
import { useQueryableRoot } from "../../hooks/useQueryableRoot";
/**
 * Provides a two pane container that can contain any type of content in its left and right panes
 * The right pane does have some built in functionality:
 *  - It is wrapped in a Box with CSS properties stickyTopContainerStyle which sticks the right pane to the top when scrolling down the page
 *  - In mobile view, the right pane is displayed in a modal when active
 */
export function TwoPane(props) {
    var _a;
    var searchData = props.searchData;
    var getQueryableRoot = useQueryableRoot();
    var _b = useState(false), isRightPaneOpen = _b[0], setIsRightPaneOpen = _b[1];
    var _c = useState((_a = props.searchData.jobs.at(0)) === null || _a === void 0 ? void 0 : _a.jobKey), selectedJobKey = _c[0], setSelectedJobKey = _c[1];
    // Sync job key with URL and handle automatic job selection
    useJobKeyUrlSync(selectedJobKey, setSelectedJobKey, searchData.jobs);
    var isMobile = useDetectMobileViewPort().isMobile;
    var twoPaneRecipe = useSlotRecipe({ key: "TwoPane" });
    var recipeProps = twoPaneRecipe.splitVariantProps({})[0];
    var styles = twoPaneRecipe(recipeProps);
    var onModalClose = function () {
        setIsRightPaneOpen(false);
    };
    var getSelectedJobCard = function () {
        return selectedJobKey
            ? getQueryableRoot().querySelector("[data-jobkey=\"".concat(selectedJobKey, "\"]"))
            : null;
    };
    var rightPaneContent = (_jsx(SearchResultsJobDetails, { jobs: searchData.jobs, selectedJobKey: selectedJobKey }));
    /**
     * On mobile, the right pane will be rendered in a Modal. At the top there will be a close button that triggers
     * the modal closing. You can pass in props here to customize how this button is displayed. Use the children prop
     * to customize the content of the button.
     */
    var backToLeftPaneButtonProps = {
        children: (_jsxs(_Fragment, { children: [_jsx(FontAwesomeIcon, { icon: faArrowLeft }), "Back to search results"] })),
    };
    /**
     * ArrowLeft inside the job details sends focus back to the open job on the
     * left, so the results can be reached without shift-tabbing back through the
     * whole description.
     */
    var handleArrowToLeftPane = function (event) {
        var _a;
        if (isMobile) {
            return;
        }
        if (event.key !== "ArrowLeft" ||
            event.altKey ||
            event.ctrlKey ||
            event.metaKey ||
            event.shiftKey) {
            return;
        }
        event.preventDefault();
        (_a = getSelectedJobCard()) === null || _a === void 0 ? void 0 : _a.focus();
    };
    return (_jsxs(Flex, __assign({ css: styles.twoPane, id: "two-pane-container" }, { children: [_jsx(Box, __assign({ css: styles.leftPane, id: "left-pane", as: "section", "aria-label": "Search results" }, { children: _jsx(SearchResultsListPanel, { searchData: searchData, setIsRightPaneOpen: setIsRightPaneOpen, selectedJobKey: selectedJobKey, setSelectedJobKey: setSelectedJobKey }) })), _jsx(Box, __assign({ css: styles.rightPane, id: "right-pane", tabIndex: 0, onKeyDown: handleArrowToLeftPane }, { children: isMobile ? (_jsx(Dialog.Root, __assign({ open: isRightPaneOpen, size: "full", motionPreset: "slide-in-bottom", finalFocusEl: getSelectedJobCard }, { children: _jsx(Portal, { children: _jsx(Dialog.Positioner, { children: _jsxs(Dialog.Content, __assign({ css: styles.modal }, { children: [_jsx(Button, __assign({ size: "lg" }, backToLeftPaneButtonProps, { onClick: onModalClose, variant: "backToLeftPanelModal" })), rightPaneContent] })) }) }) }))) : (_jsx(Box, __assign({ css: styles.stickyTopContainer, id: "right-pane-content" }, { children: rightPaneContent }))) }))] })));
}
